import {State, Action, StateContext, Store} from '@ngxs/store';
import {
  tap,
  concatMap,
  first,
  switchMap,
  map,
  catchError,
  retry,
  filter,
  take,
  timeout,
  delay,
} from 'rxjs/operators';
import {
  Basket,
  BasketTransfer,
  BasketValidation,
} from 'src/assets/chepri-modules/src/models/olo.basket';
import {BillingInfo} from 'src/assets/chepri-modules/src/models/olo.billinginfo';
import {
  CreateBasket,
  AddProductToBasket,
  RemoveProductFromBasket,
  UpdateSingleProductInBasket,
  SetTimeWanted,
  CreateFromOrder,
  ValidateBasket,
  SetCreateAccountIntent,
  SubmitBasket,
  EmptyBasket,
  SetTipAmount,
  SetBillingInfo,
  EmptyPreviousBasket,
  EmptyValidation,
  SetHandoffMode,
  SetDispatchAddress,
  GetBillingSchemes,
  CreateFromFaves,
  ApplyCoupon,
  CheckBalance,
  CleanGiftCard,
  SubmitBasketMultiPay,
  ApplyGiftCard,
  GetBasket,
  GetTimeSlots,
  SetTimeWantedToAsap,
  SetBuckets,
  SetIncrementor,
  TransferBasket,
  RemoveGiftCard,
  SaveExtraCreditCard,
  AddUpsellToBasket,
  AddManualFireTime,
  SetChange,
  ClearChange,
  ClearTrancloudCardData,
  StoreTrancloudCardData,
  SetOrderPlaceholder,
  RemoveOrderPlaceholder,
  ClearExtraCard,
  RemoveCoupon,
  SetCustomField,
  AddSavedProductToBasket,
  AddPastOrderProductToBasket,
  SyncCapacityWithBasket,
  SetReferralToken,
  StoreOloPayReceiptData,
  ClearOloPayReceiptData,
} from '../actions/basket.action';
import {BasketService} from '@app/providers/basket.service';
import {Limiters} from 'src/assets/chepri-modules/src/models/Limiter';
import {
  buildProductCategoryMap,
  CAPACITY_REACHED_MESSAGE,
  categoryLimitFromMetadata,
  categoryOfBasketProduct,
  exceedsCategoryLimits,
  limitersFromBasket,
  planReductionsToLimits,
  ProductCategoryMap,
  remainingForCategory,
  updateBodyForQuantity,
} from '@app/providers/basket-limits';
import {
  BasketChoice,
  BasketProduct,
} from 'src/assets/chepri-modules/src/models/olo.basketproduct';
import {orderstatus} from 'src/assets/chepri-modules/src/models/olo.recentorders';
import {UserState, UserStateModel} from './user.state';
import {FavoritesOrderPayloadModel} from '../../../assets/chepri-modules/src/lib/favorite-order-name/favorites-order.model';
import {GetRecentOrders} from '../actions/user.action';
import {RestaurantState} from './restaurant.state';
import {CapacityService} from '@app/providers/capacity.service';
import {SelectableTime, TimeSlot} from '@app/models/capacity.model';
import {ToastrService} from 'ngx-toastr';
import {
  of,
  concat,
  throwError,
  Observable,
  combineLatest,
  defer,
  last,
} from 'rxjs';
import moment from 'moment';
import {DirectusExpoProvider} from '@app/providers/expo/directus/directus-expo.provider';
import {Preferences} from '@capacitor/preferences';
import {ModeService} from '@app/providers/mode.service';
import {NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import {SwipeCardModalComponent} from '@app/components/swipe-card-modal/swipe-card-modal.component';
import {OloPayTerminalService} from '@app/services/olo-pay/olo-pay-terminal.service';
import {OloPayPaymentService} from '@app/services/olo-pay/olo-pay-payment.service';
import {Injectable} from '@angular/core';
import {reportHandledError} from '@app/core/sentry/sentry-report';
import {CapacityPlaceholder} from 'src/assets/chepri-modules/src/models/capacityplaceholder';
import {Order} from 'src/assets/chepri-modules/src/models/olo.order';

import {DateTime} from '@lib/models/DateTime';
import {AnalyticsService} from '@app/providers/analytics.service';
import {CredentialsService} from '@app/core/authentication/credentials.service';
import {GlobalStateModel} from '@app/store/state.model';
import {
  GetPaymentSessionStatusResponse,
  PaymentSessionStatus,
} from '@app/models/olo.get-payment-session-status-response';
import {BillingAccount} from '@app/models/billingaccount.interface';

export interface BasketStateModel {
  basket: Basket;
  billingInfo: BillingInfo;
  capacity: Limiters;
  buckets: SelectableTime[];
  timeSlots: TimeSlot[];
  previousBasket: Order;
  basketValidation: BasketValidation;
  billingSchemes: any;
  giftCard: any;
  applyGiftCard: any;
  incrementor: any;
  extraCard: any;
  manualFireTime: any;
  change: number;
  trancloudInfo: {
    cardNumber: string;
    cardType: string;
    authCode: string;
    refNo: string;
  };
  oloPayReceipt?: {
    cardNumber: string;
    cardType: string;
    authCode: string;
    refNo: string;
  };
  capacityPlaceholder: CapacityPlaceholder;
  // Olo Accounts opt-in captured at guest-info time (alongside billingInfo).
  // Read at SubmitBasket time to append `oloaccount.createaccount` +
  // `confirmationtoken` to the basket-submit body. Cleared by
  // buildPostSubmitPatch on a successful submit.
  createAccountOptedIn: boolean;
  oloAccountConfirmationToken: string | null;
  oloAccountVerificationMethod: 'sms' | 'email' | null;
}

// Post-submit clearing invariant. Called by every Submit* action when archiving the
// current basket to previousBasket. Spec: openspec/specs/basket-state-post-submit-clearing/spec.md.
// Keeping this out-of-class so multi-pay and single-CC cannot drift.
// NOTE: `trancloudInfo`, `oloPayReceipt`, and `change` are intentionally NOT cleared
// here — the confirmation screen renders them. They are cleared on next-order
// navigation (`EmptyPreviousBasket`), and `change` is additionally cleared on
// confirmation teardown (`ClearChange` in confirmation's ngOnDestroy).
function buildPostSubmitPatch(
  previousBasket: Order
): Partial<BasketStateModel> {
  return {
    previousBasket,
    basket: null,
    capacity: new Limiters([]),
    giftCard: null,
    applyGiftCard: null,
    extraCard: null,
    basketValidation: null,
    createAccountOptedIn: false,
    oloAccountConfirmationToken: null,
    oloAccountVerificationMethod: null,
  };
}

/**
 * Thrown by SubmitBasket after Olo rejected the order AND this action already
 * voided the terminal's payment session. Callers compare against it so they
 * don't send a second void, which would fail as already-cancelled and falsely
 * report a live authorization.
 */
export const PAYMENT_AUTHORIZATION_VOIDED =
  'Order could not be submitted. Payment authorization was cancelled.';

@State<BasketStateModel>({
  name: 'basket',
  defaults: {
    basket: null,
    billingInfo: new BillingInfo(),
    capacity: new Limiters([]),
    buckets: null,
    timeSlots: null,
    previousBasket: null,
    basketValidation: null,
    billingSchemes: null,
    giftCard: null,
    applyGiftCard: null,
    incrementor: null,
    extraCard: null,
    manualFireTime: null,
    change: null,
    trancloudInfo: null,
    capacityPlaceholder: null,
    createAccountOptedIn: false,
    oloAccountConfirmationToken: null,
    oloAccountVerificationMethod: null,
  },
})
@Injectable({
  providedIn: 'root',
})
export class BasketState {
  basketSubmit: BillingInfo = new BillingInfo();
  isLoading = false;

  constructor(
    private basketService: BasketService,
    private store: Store,
    private userState: UserState,
    private capacityService: CapacityService,
    private toast: ToastrService,
    private expo: DirectusExpoProvider,
    private mode: ModeService,
    private modalService: NgbModal,
    private oloPayTerminal: OloPayTerminalService,
    private oloPayPayment: OloPayPaymentService,
    private toastr: ToastrService,

    private analytics: AnalyticsService,
    private credentials: CredentialsService
  ) {}

  /**
   * A guest credential (guest-info's `{isGuest: true}` in sessionStorage)
   * only exists to lift GuestGuard for the order being built. Once Olo has
   * accepted that order it is spent: leaving it in place let the same tab
   * skip the guest-info page on the next order while EmptyPreviousBasket
   * had already wiped billingInfo, so the second submit reached Olo with no
   * usertype ("usertype may be 'user' or 'guest'"). Signed-in and kiosk
   * credentials are not guest credentials and are left alone.
   *
   * Runs after the customer has been charged, so it must never throw.
   */
  private releaseGuestSession(): void {
    try {
      if (this.credentials.isGuest()) {
        Promise.resolve(this.credentials.setCredentials(null, false)).catch(
          () => {}
        );
      }
    } catch {
      // Best effort: a storage failure must not derail the confirmation.
    }
  }

  /** Olo product id -> category cap, from the currently loaded menu. */
  private productCategoryMap(): ProductCategoryMap {
    const menu = this.store.selectSnapshot(
      (s: GlobalStateModel) => s.restaurant?.menu
    );
    return buildProductCategoryMap(menu);
  }

  /** Kiosk (kms) devices are staff-operated and have no per-order cap. */
  private isUnlimitedMode(): boolean {
    return this.mode?.getStoredMode?.() === 'kms';
  }

  /**
   * The ONLY way this state stores a basket. Recomputes `capacity` from the
   * basket so the two can never disagree.
   */
  private applyBasket(
    patchState: (val: Partial<BasketStateModel>) => void,
    basket: Basket,
    extra: Partial<BasketStateModel> = {}
  ): void {
    patchState({
      ...extra,
      basket,
      capacity: limitersFromBasket(
        basket,
        this.productCategoryMap(),
        this.isUnlimitedMode()
      ),
    });
  }

  /**
   * Bring a basket Olo just returned back within every cap by reducing
   * quantities (newest lines first). Quantity-based, so it copes with Olo
   * coalescing an add into an existing line and with two adds racing on
   * the same stale `before`. Emits the corrected basket and whether
   * anything had to change.
   */
  private reduceToLimits(
    after: Basket,
    before: Basket | null,
    categoryMap: ProductCategoryMap
  ): Observable<{basket: Basket; trimmed: boolean}> {
    const plan = planReductionsToLimits(
      after,
      before,
      categoryMap,
      this.isUnlimitedMode()
    );
    if (!plan.length) {
      return of({basket: after, trimmed: false});
    }
    const ops = plan.map(({line, newQuantity}) =>
      defer(() =>
        newQuantity <= 0
          ? this.basketService.removeProductFromBasket(
              line.id.toString(),
              after.id
            )
          : this.basketService.updateProductInBasket(
              line.id.toString(),
              after.id,
              updateBodyForQuantity(line, newQuantity)
            )
      )
    );
    return concat(...ops).pipe(
      last(),
      catchError(() => this.basketService.getBasket(after.id)),
      map((basket: Basket) => ({basket, trimmed: true}))
    );
  }

  /**
   * For adds: if a concurrent change pushed the basket over a cap, undo
   * the excess, store what Olo holds now, and fail the action.
   */
  private rollBackIfOverLimit(
    patchState: (val: Partial<BasketStateModel>) => void,
    after: Basket,
    before: Basket | null,
    categoryMap: ProductCategoryMap
  ): Observable<Basket> {
    return this.reduceToLimits(after, before, categoryMap).pipe(
      switchMap(({basket, trimmed}) => {
        if (!trimmed) {
          return of(basket);
        }
        this.applyBasket(patchState, basket);
        return throwError(() => new Error(CAPACITY_REACHED_MESSAGE));
      })
    );
  }

  /**
   * For whole-order reorders: a past order may hold more than today's cap.
   * Trim it to fit, warn, and continue with what fits.
   */
  private trimToLimits(
    basket: Basket,
    categoryMap: ProductCategoryMap
  ): Observable<Basket> {
    return this.reduceToLimits(basket, null, categoryMap).pipe(
      map(({basket: trimmedBasket, trimmed}) => {
        if (trimmed) {
          this.toast?.warning(
            'Some items were reduced to stay within the per-order limit.'
          );
        }
        return trimmedBasket;
      })
    );
  }

  @Action(SyncCapacityWithBasket)
  syncCapacityWithBasket({
    patchState,
    getState,
  }: StateContext<BasketStateModel>) {
    this.applyBasket(patchState, getState().basket);
  }

  @Action(CreateBasket)
  createBasket(
    {patchState}: StateContext<BasketStateModel>,
    {locationId, authToken}: CreateBasket
  ) {
    if (!authToken) {
      const userState: UserStateModel = this.store.selectSnapshot(
        (state) => state.user
      );
      if (userState && userState.info && userState.info.authtoken) {
        authToken = userState.info.authtoken;
      }
    }
    return this.basketService.createBasket(locationId, authToken).pipe(
      tap(
        (res) => {
          const basket = res;
          Preferences.set({key: 'punchBasket', value: basket.id});
          this.applyBasket(patchState, basket);
          this.store.dispatch(new EmptyValidation());
          if (sessionStorage.getItem('rwg_token')) {
            this.store.dispatch(new SetReferralToken());
          }
        }
        // error => {
        //   this.toast.warning(error.error.message);
        // }
      )
    );
  }

  @Action(GetBasket)
  async getBasket(
    {getState, patchState}: StateContext<BasketStateModel>,
    {payload}: GetBasket
  ) {
    const bId = await Preferences.get({key: 'punchBasket'});
    let basketId = bId.value;
    if (getState().basket) {
      basketId = getState().basket.id;
    }
    // const basketId = getState().basket.id;
    return this.basketService
      .getBasket(basketId)
      .pipe(
        first(),
        tap((basket: any) => {
          this.applyBasket(patchState, basket);
        })
      )
      .subscribe();
  }

  @Action(GetTimeSlots)
  getTimeSlots(
    {getState, patchState}: StateContext<BasketStateModel>,
    {isFirstLoad, deliveryMode}: any
  ) {
    const capacitySettings = this.store.selectSnapshot(
      (state) => state.restaurant
    ).capacity_settings;
    const earliestRT = getState().basket.earliestreadytime;
    const end = this.store.selectSnapshot((state) => state.restaurant).calendar
      .calendar[0].ranges[0].end;
    const limiters = getState().capacity;
    const vendorId = getState().basket.vendorid;
    const delivMode = deliveryMode
      ? deliveryMode
      : getState().basket.deliverymode;
    const checkBuzzCapacity: boolean =
      delivMode === 'pickup' && localStorage.getItem('modeKey') === 'order';
    return this.capacityService
      .getAvailableTimeSlots(
        capacitySettings,
        earliestRT,
        end,
        vendorId,
        limiters,
        isFirstLoad,
        checkBuzzCapacity
      )
      .pipe(
        tap((timeSlots) => {
          patchState({
            timeSlots,
          });
        })
      );
  }

  @Action(SetBuckets)
  setBuckets(
    {patchState}: StateContext<BasketStateModel>,
    {buckets, isManualFire}: SetBuckets
  ) {
    patchState({
      buckets,
    });
    this.store.dispatch(
      new SetTimeWanted(buckets[buckets.length - 1].time, isManualFire)
    );
  }

  @Action(CreateFromOrder)
  createFromOrder(
    {patchState}: StateContext<BasketStateModel>,
    payload: CreateFromOrder
  ) {
    return this.basketService
      .createBasketFromOrder(payload.orderRef, payload.id, payload.token)
      .pipe(
        switchMap((basket) =>
          this.trimToLimits(basket, this.productCategoryMap())
        ),
        tap((basket) => {
          this.applyBasket(patchState, basket);
          this.store.dispatch(new GetTimeSlots(true));
        })
      );
  }

  @Action(CreateFromFaves)
  CreateFromFaves(
    ctx: StateContext<BasketStateModel>,
    {payload}: {payload: FavoritesOrderPayloadModel}
  ) {
    return this.basketService
      .reorderFavoriteOrder(payload.id, payload.token)
      .pipe(
        switchMap((basket: Basket) =>
          this.trimToLimits(basket, this.productCategoryMap())
        ),
        tap((basket) => {
          this.applyBasket(ctx.patchState, basket);
          this.store.dispatch(new GetTimeSlots(false));
        })
      );
  }

  @Action(SetHandoffMode)
  setPickUp(
    {getState, patchState}: StateContext<BasketStateModel>,
    action: SetHandoffMode
  ) {
    const basketId = getState().basket.id;
    return this.basketService
      .setDeliveryMode(action.handoffMode, basketId)
      .pipe(
        tap(
          (res: Basket) => {
            this.applyBasket(patchState, res);
            if (action.handoffMode === 'dispatch') {
              const location = this.store.selectSnapshot(
                (state) => state.restaurant.location
              );
              this.store.dispatch(new SetDispatchAddress(location, basketId));
            }
          },
          (error) => {
            this.toast.warning(error.error.message);
          }
        )
      );
  }

  @Action(SetDispatchAddress)
  setDispatchAddress(
    {patchState}: StateContext<BasketStateModel>,
    {addressInfo, basketId}: SetDispatchAddress
  ) {
    const body = {
      streetaddress: addressInfo.streetAddress,
      city: addressInfo.city,
      zipcode: addressInfo.zip,
      specialinstructions: addressInfo.specialInstructions,
    };
    return this.basketService.setDeliveryAddress(body, basketId).pipe(
      tap((basket: Basket) => {
        this.applyBasket(patchState, basket);
      })
    );
  }

  @Action(AddProductToBasket)
  addProductToBasket(
    {patchState, getState}: StateContext<BasketStateModel>,
    {product, metadata}: AddProductToBasket
  ) {
    const state = getState();
    const before = state.basket;
    const limit = categoryLimitFromMetadata(metadata);

    if (!limit) {
      // Not a capped category: add and store.
      return this.basketService
        .addProductToBasket(product, before.id, null)
        .pipe(
          tap((basket) => {
            this.applyBasket(patchState, basket);
            this.analytics.logAddToCart([
              basket.products[basket.products.length - 1],
            ]);
          })
        );
    }

    const map = this.productCategoryMap();
    const remaining = remainingForCategory(
      before,
      map,
      limit.categoryId,
      this.isUnlimitedMode(),
      limit.max
    );
    if (product.quantity > remaining) {
      return throwError(() => new Error(CAPACITY_REACHED_MESSAGE));
    }

    return this.basketService
      .addProductToBasket(product, before.id, limit.categoryId)
      .pipe(
        switchMap((basket) =>
          this.rollBackIfOverLimit(patchState, basket, before, map)
        ),
        tap((basket) => {
          this.applyBasket(patchState, basket);
          this.store.dispatch(new GetTimeSlots(false));
          this.analytics.logAddToCart([
            basket.products[basket.products.length - 1],
          ]);
        })
      );
  }

  @Action(AddSavedProductToBasket)
  addSavedProductToBasket(
    ctx: StateContext<BasketStateModel>,
    action: AddSavedProductToBasket
  ) {
    const before = ctx.getState().basket;
    const saved = action.product.product;
    const limit = categoryLimitFromMetadata(action.metadata);
    const add$ = this.basketService.addSingleProductByChainIDToBasket(
      before.id,
      saved.chainproductid,
      saved.quantity,
      saved.choices,
      saved.specialinstructions,
      saved.recipient
    );

    if (!limit) {
      return add$.pipe(
        tap((basket) => {
          this.applyBasket(ctx.patchState, basket);
          this.analytics.logAddToCart([
            basket.products[basket.products.length - 1],
          ]);
        })
      );
    }

    const map = this.productCategoryMap();
    const remaining = remainingForCategory(
      before,
      map,
      limit.categoryId,
      this.isUnlimitedMode(),
      limit.max
    );
    if (saved.quantity > remaining) {
      return throwError(() => new Error(CAPACITY_REACHED_MESSAGE));
    }

    return add$.pipe(
      switchMap((basket) =>
        this.rollBackIfOverLimit(ctx.patchState, basket, before, map)
      ),
      tap((basket) => {
        this.applyBasket(ctx.patchState, basket);
        this.store.dispatch(new GetTimeSlots(false));
        this.analytics.logAddToCart([
          basket.products[basket.products.length - 1],
        ]);
      })
    );
  }

  @Action(AddPastOrderProductToBasket)
  addPastOrderProductToBasket(
    ctx: StateContext<BasketStateModel>,
    action: AddPastOrderProductToBasket
  ) {
    const before = ctx.getState().basket;
    const pastProduct = action.pastOrderProduct;
    // Chain ids work for both same-location and cross-location reorders.
    const choices = pastProduct.choices.map((choice) => ({
      chainchoiceid: choice.chainchoiceid,
      quantity: 1,
    }));
    const limit = categoryLimitFromMetadata(action.metadata);
    const map = this.productCategoryMap();
    let quantityToAdd = pastProduct.quantity;
    let customdata: string = null;

    if (limit) {
      customdata = String(limit.categoryId);
      const remaining = remainingForCategory(
        before,
        map,
        limit.categoryId,
        this.isUnlimitedMode(),
        limit.max
      );
      // Reorders add what still fits (partial add), same as before.
      quantityToAdd = Math.min(remaining, pastProduct.quantity);
      if (quantityToAdd <= 0) {
        return throwError(() => new Error(CAPACITY_REACHED_MESSAGE));
      }
    }

    return this.basketService
      .addSingleProductByChainIDToBasket(
        before.id,
        pastProduct.chainproductid,
        quantityToAdd,
        choices,
        pastProduct.specialinstructions,
        undefined,
        customdata
      )
      .pipe(
        switchMap((basket) =>
          this.rollBackIfOverLimit(ctx.patchState, basket, before, map)
        ),
        tap((basket) => {
          this.applyBasket(ctx.patchState, basket);
          this.store.dispatch(new GetTimeSlots(false));
          this.analytics.logAddToCart([
            basket.products[basket.products.length - 1],
          ]);
        })
      );
  }

  @Action(SetTipAmount)
  SetTipAmount(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    const state = getState();

    return this.basketService.addTip(payload.payload, state.basket.id).pipe(
      tap((res) => {
        const basket: Basket = res;
        this.applyBasket(patchState, basket);
        this.store.dispatch(new ValidateBasket());
      })
    );
  }

  @Action(RemoveProductFromBasket)
  removeProductFromBasket(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    const state = getState();
    const basketProd = state.basket.products.find(
      (p: BasketProduct) => p.id == payload.payload
    ); // Double equal: Olo returns numeric ids, the action carries a string

    return this.basketService
      .removeProductFromBasket(payload.payload, state.basket.id)
      .pipe(
        tap((basket) => {
          this.applyBasket(patchState, basket);
          this.store.dispatch(new GetTimeSlots(false));
          this.store.dispatch(new ValidateBasket());
          if (basketProd) {
            this.analytics.logRemoveFromCart(basketProd);
          }
        })
      );
  }

  @Action(UpdateSingleProductInBasket)
  updateSingleProductInBasket(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: UpdateSingleProductInBasket
  ) {
    const state = getState();
    const basket = state.basket;
    const body = {...payload.body};
    const current = (basket?.products || []).find(
      (p: BasketProduct) => p.id.toString() === payload.basketProductId
    );
    if (!current) {
      return throwError(
        () => new Error('That item is no longer in your order.')
      );
    }

    const map = this.productCategoryMap();
    const limit = categoryOfBasketProduct(current, map);
    const targetQuantity = Number(body.quantity);
    const quantDifference = targetQuantity - current.quantity;

    if (limit) {
      body.customdata = limit.categoryId.toString();
      if (quantDifference > 0) {
        const remaining = remainingForCategory(
          basket,
          map,
          limit.categoryId,
          this.isUnlimitedMode(),
          limit.max
        );
        if (quantDifference > remaining) {
          this.toast.warning(CAPACITY_REACHED_MESSAGE);
          return of(basket);
        }
      }
    } else if (current.customdata !== undefined) {
      body.customdata = current.customdata;
    }

    return this.basketService
      .updateProductInBasket(payload.basketProductId, basket.id, body)
      .pipe(
        switchMap((updated: Basket) => {
          // Only an increase can push the basket over the cap. A decrease is
          // always accepted, even when the basket is still over (that is how
          // a customer works an over-cap basket back down).
          if (
            quantDifference <= 0 ||
            !exceedsCategoryLimits(updated, map, this.isUnlimitedMode()).length
          ) {
            return of(updated);
          }
          // A concurrent change pushed the basket over the cap: put the
          // quantity back, store whatever Olo says now, and fail.
          return this.basketService
            .updateProductInBasket(payload.basketProductId, basket.id, {
              ...body,
              quantity: current.quantity,
            })
            .pipe(
              catchError(() => this.basketService.getBasket(basket.id)),
              tap((restored: Basket) => this.applyBasket(patchState, restored)),
              switchMap(() =>
                throwError(() => new Error(CAPACITY_REACHED_MESSAGE))
              )
            );
        }),
        tap((updated: Basket) => {
          this.applyBasket(patchState, updated);
          this.store.dispatch(new GetTimeSlots(false));
        })
      );
  }

  @Action(SetTimeWanted)
  setTimeWanted(
    {patchState, getState}: StateContext<BasketStateModel>,
    {dateTime, isManualFire}: SetTimeWanted
  ) {
    const state = getState();
    const momentTime = dateTime.moment;
    let body;
    if (typeof momentTime === 'string' || momentTime instanceof String) {
      const newDateTime = new DateTime(moment(momentTime));
      // const offsetTime = moment().utcOffset() / 60;
      // const ye = '0' + momentTime.substring(0, 4);
      // const mon = momentTime.substring(5, 7);
      // const today = momentTime.substring(8, 10);
      // const hr = ((Number(momentTime.substring(11, 13)) + offsetTime) < 0 ? (Number(momentTime.substring(11, 13)) + offsetTime) + 24 : (Number(momentTime.substring(11, 13)) + offsetTime)).toString();
      // const min = momentTime.substring(14, 16);
      body = {
        ismanualfire: isManualFire || false,
        ...newDateTime.oloTimeWanted,
      };
    } else {
      body = {
        ismanualfire: isManualFire || false,
        ...dateTime.oloTimeWanted,
      };
    }

    return this.basketService.setTimeWanted(state.basket.id, body).pipe(
      tap(
        (basket) => {
          if (isManualFire) {
            const formatManualFire = moment(momentTime).format(
              'YYYY-MM-DD HH:mm:ss'
            );
            this.store.dispatch(new AddManualFireTime(formatManualFire));
          }
          this.store.dispatch(new RemoveOrderPlaceholder());
          this.applyBasket(patchState, basket);
        }
        // error => {
        //   this.toast.warning(error.error.message);
        // }
      )
    );
  }

  @Action(SetTimeWantedToAsap)
  setTimeWantedToAsap(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    const state = getState();
    return this.basketService.setTimeWantedToAsap(state.basket.id).pipe(
      tap(
        (basket) => {
          this.applyBasket(patchState, basket);
        }
        // error => {
        //   this.toast.warning(error.error.message);
        // }
      )
    );
  }

  @Action(ApplyCoupon)
  applyCoupon(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    const state = getState();
    return this.basketService
      .applyCoupon(payload.payload, state.basket.id)
      .pipe(
        tap((res) => {
          const basket = res;
          this.applyBasket(patchState, basket);
        })
      );
  }

  @Action(RemoveCoupon)
  removeCoupon(ctx: StateContext<BasketStateModel>, action: RemoveCoupon) {
    return this.basketService.removeCoupon(ctx.getState().basket.id).pipe(
      map((res) => {
        ctx.dispatch(new ValidateBasket());
        return this.applyBasket(ctx.patchState, res);
      })
    );
  }

  @Action(ApplyGiftCard)
  applyGiftCard(
    {patchState, getState}: StateContext<BasketState>,
    payload: any
  ) {
    patchState({
      applyGiftCard: payload.payload,
    });
  }

  @Action(SetIncrementor)
  setIncrememtor(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    patchState({incrementor: payload.payload});
  }

  @Action(AddManualFireTime)
  addManualFireTime(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    patchState({manualFireTime: payload.payload});
  }

  @Action(GetBillingSchemes)
  getBillingSchemes(
    {getState, patchState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    const basketId = getState().basket.id;
    return this.basketService.getBillingSchemes(basketId).pipe(
      tap((billingSchemes) => {
        patchState({billingSchemes});
      })
    );
  }

  @Action(SetBillingInfo)
  setBillingInfo(
    {getState, patchState}: StateContext<BasketStateModel>,
    {billingInfo}: SetBillingInfo
  ) {
    patchState({
      billingInfo,
    });
  }

  @Action(SetCreateAccountIntent)
  setCreateAccountIntent(
    {patchState}: StateContext<BasketStateModel>,
    {optedIn, token, method}: SetCreateAccountIntent
  ) {
    patchState({
      createAccountOptedIn: optedIn && !!token,
      oloAccountConfirmationToken: optedIn ? token : null,
      oloAccountVerificationMethod: optedIn ? method : null,
    });
  }

  @Action(CheckBalance)
  checkBalance(
    {getState, patchState}: StateContext<BasketStateModel>,
    payload: any
  ) {
    const basket = getState();

    // More robust gift card scheme lookup
    const giftCardScheme = basket.billingSchemes.find(
      (sche: any) =>
        sche.type?.toLowerCase() === 'giftcard' ||
        sche.type?.toLowerCase() === 'storedvalue'
    );

    if (!giftCardScheme) {
      return throwError(() => new Error('Gift card billing scheme not found'));
    }

    return this.basketService
      .checkGiftBalance(giftCardScheme.id, basket.basket.id, payload.payload)
      .pipe(
        tap((res: any) => {
          if (res.balance > 0) {
            const giftBalance = {
              ...res,
              cardnumber: payload.payload.cardnumber,
              pin: payload.payload.pin,
            };

            // Check if this gift card is already applied
            const existingGiftCards = basket.giftCard || [];
            const isDuplicate = existingGiftCards.some(
              (card: any) => card.cardnumber === giftBalance.cardnumber
            );

            if (isDuplicate) {
              // Don't add duplicate, just update the existing one
              const updatedCards = existingGiftCards.map((card: any) =>
                card.cardnumber === giftBalance.cardnumber ? giftBalance : card
              );
              patchState({giftCard: updatedCards});
            } else {
              // Add new gift card
              if (basket.giftCard) {
                patchState({giftCard: [...basket.giftCard, giftBalance]});
              } else {
                patchState({giftCard: [giftBalance]});
              }
            }
          }
        })
      );
  }

  @Action(RemoveGiftCard)
  removeGiftCard(
    {getState, patchState}: StateContext<BasketStateModel>,
    index: any
  ) {
    const basket = getState();
    if (index.payload === 0 && basket.giftCard.length === 1) {
      patchState({giftCard: null});
    } else if (index.payload === 0 && basket.giftCard.length === 2) {
      patchState({giftCard: [basket.giftCard[1]]});
    } else if (index.payload === 1) {
      patchState({giftCard: [basket.giftCard[0]]});
    }
  }

  @Action(SaveExtraCreditCard)
  saveExtraCreditCard(
    {getState, patchState}: StateContext<BasketStateModel>,
    card: any
  ) {
    patchState({extraCard: card.payload});
  }

  @Action(SetOrderPlaceholder)
  setOrderPlaceholder({patchState, getState}: StateContext<BasketStateModel>) {
    const state = getState();
    const restaurantId = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.restaurant.restaurant
    ).id;
    let enzoQuant = 0;
    const restaurant = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.restaurant
    );
    state.basket.products.forEach((p: BasketProduct) => {
      p.choices.forEach((c: BasketChoice) => {
        if (c.name === restaurant.settings.enzo_modifier_name) {
          enzoQuant = enzoQuant + 1 * p.quantity;
        }
      });
    });
    return this.capacityService
      .createOrUpdateOrderMetaDataFromBuckets(
        restaurantId,
        state.basket.id,
        state.buckets,
        true,
        enzoQuant,
        state.basket.deliverymode === 'pickup' &&
          localStorage.getItem('modeKey') === 'order'
      )
      .pipe(
        tap((order: any) => {
          const time = order[0].data.created_on;
          const ids: string[] = [];
          order.forEach((o: any) => {
            ids.push(o.data.id.toString());
          });
          const placeholder: CapacityPlaceholder = {
            time,
            directusId: ids,
          };
          patchState({capacityPlaceholder: placeholder});
        })
      );
  }

  @Action(RemoveOrderPlaceholder)
  RemoveOrderPlaceholder({
    patchState,
    getState,
  }: StateContext<BasketStateModel>) {
    /*
    const state = getState();
    if (state.capacityPlaceholder && state.capacityPlaceholder.directusId.length) {
      return combineLatest(state.capacityPlaceholder.directusId.map(id => {
        console.log('id', id);

          return this.capacityService.removeOldOrder(id);
        }
      )).pipe(tap(() => {
        patchState({ capacityPlaceholder: null });
      }))
    } else {
      patchState({ capacityPlaceholder: null });
    }
    */
    patchState({capacityPlaceholder: null});
  }

  @Action(ValidateBasket)
  validateBasket(
    {patchState, getState}: StateContext<BasketStateModel>,
    payload: ValidateBasket
  ) {
    const state = getState();
    return this.basketService
      .validateBasket(state.basket.id, payload.checkUpsell)
      .pipe(
        switchMap((basketValidation) => {
          if (payload.isCheckout) {
            return this.store
              .dispatch(new GetBasket())
              .pipe(map(() => patchState({basketValidation})));
          } else {
            this.store.dispatch(new GetBasket());
            return of(patchState({basketValidation}));
          }
        })
      );
  }

  @Action(SubmitBasket)
  submitBasket(
    {patchState, getState, setState, dispatch}: StateContext<BasketStateModel>,
    payload: any
  ) {
    const state = getState();
    // After a successful submit the basket is nulled (buildPostSubmitPatch).
    // A second click must fail with a clear message instead of a TypeError —
    // and must never reach Olo.
    if (!state.basket) {
      return throwError(
        () =>
          new Error(
            'There is no active order to submit. If you just placed an order, it may have gone through — please check your email for a confirmation before ordering again.'
          )
      );
    }
    const body: BillingInfo = {...state.billingInfo};
    let authtoken: string = null;
    const user = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.user
    );
    if (!!user && !!user.info && !!user.info.authtoken) {
      authtoken = user.info.authtoken;
      body.usertype = 'user';
    }
    const restaurantId = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.restaurant.restaurant
    ).id;
    const capacitySettings = this.store.selectSnapshot(
      (state) => state.restaurant
    ).capacity_settings;
    const buckets = state.buckets;
    const currentBucketIds = state?.capacityPlaceholder?.directusId || null;
    const checkBuzzCapacity: boolean =
      getState().basket.deliverymode === 'pickup' &&
      localStorage.getItem('modeKey') === 'order';
    return this.capacityService
      .checkLastSecond(
        capacitySettings,
        restaurantId,
        buckets,
        currentBucketIds,
        state?.basket?.products,
        checkBuzzCapacity
      )
      .pipe(
        concatMap((res) => {
          if (res.canFit) {
            console.log('submitBasket 922');
            return this.submitAndSendData(
              body,
              authtoken,
              restaurantId,
              buckets,
              user,
              {
                patchState,
                getState,
                setState,
                dispatch,
              },
              payload?.payload?.tableNumber,
              '',
              null,
              null,
              null,
              null,
              '',
              null,
              null
            );
          } else {
            if (!res.enzoError) {
              this.analytics.logTimeSlotFull();
            }
            return throwError(
              new Error(
                res.enzoError
                  ? 'Sorry, Gluten-Free is sold out. To proceed with your order please remove Gluten-Free from your basket.'
                  : 'The order time selected is no longer available, please choose a different time'
              )
            ).pipe(
              tap(() => {
                this.store.dispatch(new GetTimeSlots(false)).subscribe(() => {
                  const timeSlots = getState().timeSlots;
                  this.store.dispatch(
                    new SetBuckets(timeSlots[0].value.buckets)
                  );
                });
              })
            );
          }
        })
      );
  }

  @Action(SubmitBasketMultiPay)
  submitBasketMultiPay(
    {patchState, getState, dispatch}: StateContext<BasketStateModel>,
    payload: SubmitBasketMultiPay
  ) {
    const state = getState();
    // Same double-submit guard as SubmitBasket: the basket is nulled after a
    // successful submit, so fail fast with a clear message.
    if (!state.basket) {
      return throwError(
        () =>
          new Error(
            'There is no active order to submit. If you just placed an order, it may have gone through — please check your email for a confirmation before ordering again.'
          )
      );
    }
    const capacitySettings = this.store.selectSnapshot(
      (state) => state.restaurant
    ).capacity_settings;
    const buckets = state.buckets;
    const basket = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.basket.billingInfo
    );
    const user = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.user
    );
    const restaurantId = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.restaurant.restaurant
    ).id;
    let authtoken: string = null;
    let userType = basket.usertype;
    let firstName = basket.firstname;
    let lastName = basket.lastname;
    let email = basket.emailaddress;
    let contactNumber = basket.contactnumber;

    if (!!user && !!user.info && !!user.info.authtoken) {
      authtoken = user.info.authtoken;
      userType = 'user';
      firstName = user.info.firstname;
      lastName = user.info.lastname;
      email = user.info.emailaddress;
      contactNumber = user.info.contactnumber;
    }

    const updatedBillingInfo = {
      ...state.billingInfo,
      billingaccounts: payload.payload,
    };
    dispatch(new SetBillingInfo(updatedBillingInfo));

    const body: Partial<BillingInfo> = {
      usertype: userType,
      firstname: firstName,
      lastname: lastName,
      emailaddress: email,
      contactnumber: contactNumber,
      billingaccounts: payload.payload,
    };
    this.appendOloAccountIntent(body, state);
    const directusIds = state.capacityPlaceholder?.directusId || null;
    const checkBuzzCapacity: boolean =
      getState().basket.deliverymode === 'pickup' &&
      localStorage.getItem('modeKey') === 'order';
    return this.capacityService
      .checkLastSecond(
        capacitySettings,
        restaurantId,
        buckets,
        directusIds,
        state.basket?.products,
        checkBuzzCapacity
      )
      .pipe(
        concatMap((res) => {
          if (!res.canFit) {
            /* unchanged “slot full” branch */
            if (!res.enzoError) {
              this.analytics.logTimeSlotFull();
            }
            return throwError(
              () =>
                new Error(
                  res.enzoError
                    ? 'Sorry, Gluten-Free is sold out. To proceed with your order please remove Gluten-Free from your basket.'
                    : 'The order time selected is no longer available, please choose a different time'
                )
            ).pipe(
              tap(() => {
                this.store.dispatch(new GetTimeSlots(false)).subscribe(() => {
                  const timeSlots = getState().timeSlots;
                  this.store.dispatch(
                    new SetBuckets(timeSlots[0].value.buckets)
                  );
                });
              })
            );
          }

          return this.basketService
            .submitOrderMultiPay(state.basket.id, body, authtoken)
            .pipe(
              switchMap((previousBasket: Order) => {
                this.acknowledgeOloAccountCreated(state, previousBasket);
                // Persist immediately so UI can navigate to confirmation even if downstream calls fail.
                // Uses the shared post-submit patch so multi-pay and single-CC stay in lockstep — see
                // openspec/changes/fix-terminal-residual-state. Do NOT reintroduce a `...getState()`
                // spread here; it silently preserves fields added later to BasketStateModel.
                patchState(buildPostSubmitPatch(previousBasket));
                this.releaseGuestSession();
                try {
                  sessionStorage.setItem(
                    'punchPreviousOrder',
                    previousBasket.oloid
                  );
                } catch {}
                /* housekeeping / capacity meta – unchanged */
                Preferences.remove({key: 'punchBasket'});
                // The customer has already been charged: nothing below may
                // throw out of this switchMap, or the action errors and the
                // CMS never receives the order.
                let enzoQuant = 0;
                try {
                  const restaurant = this.store.selectSnapshot(
                    (s: GlobalStateModel) => s.restaurant
                  );
                  state.basket.products.forEach((p: BasketProduct) => {
                    p.choices.forEach((c: BasketChoice) => {
                      if (c.name === restaurant.settings.enzo_modifier_name) {
                        enzoQuant += p.quantity;
                      }
                    });
                  });
                } catch (e) {
                  console.error('Failed to compute enzo quantity', e);
                }

                // NOTE: Do NOT dispatch CreateBasket here. Creating a fresh basket
                // while the user is still on checkout allows a second click to submit
                // a real duplicate order. The next order flow will create one.
                dispatch(new GetRecentOrders());

                const cardNumber = payload.cardNumber;
                const cardType = payload.cardType;
                const authCode = payload.authCode;
                const refNo = payload.refNo;
                const recordNo = payload.recordNo;

                dispatch(
                  new StoreTrancloudCardData(
                    cardNumber,
                    cardType,
                    authCode,
                    refNo
                  )
                );

                // Create Expo order with retries - wait briefly but don't block indefinitely.
                // Operator order is timeout → retry → catchError so each retry attempt gets
                // its own 5 s budget. See openspec/changes/fix-expo-metadata-per-call-timeout.
                // defer() so a synchronous throw while building the request
                // body is routed into catchError + logError below instead of
                // escaping the switchMap post-charge.
                const expoCall$ = defer(() =>
                  this.expo.postExpoOrder(
                    previousBasket,
                    user,
                    state.manualFireTime,
                    payload?.tableNumber,
                    '', // invoiceNumber – still blank in kiosk flow
                    cardNumber,
                    cardType,
                    authCode,
                    refNo,
                    recordNo,
                    null,
                    null
                  )
                ).pipe(
                  timeout(5000), // 5 second per-attempt timeout
                  retry(3),
                  catchError((error) => {
                    console.error('Error in postExpoOrder (MultiPay):', error);

                    // Log error to Directus for monitoring
                    this.expo
                      .logError(
                        'expo_service',
                        error.message || error.toString(),
                        {
                          oloId: previousBasket.oloid,
                          basketId: state.basket.id,
                          restaurantId: restaurantId,
                        }
                      )
                      .subscribe();

                    // Don't block customer experience for backend expo metadata failures
                    return of(null);
                  })
                );

                // Create capacity metadata with retries - wait briefly but don't block indefinitely.
                // Operator order is timeout → retry → catchError so each retry attempt gets
                // its own 5 s budget. See openspec/changes/fix-expo-metadata-per-call-timeout.
                const capacityCall$ = defer(() =>
                  this.capacityService.createOrUpdateOrderMetaDataFromBuckets(
                    restaurantId,
                    previousBasket.oloid,
                    buckets,
                    false,
                    enzoQuant,
                    state.basket.deliverymode === 'pickup' &&
                      localStorage.getItem('modeKey') === 'order',
                    directusIds
                  )
                ).pipe(
                  timeout(5000), // 5 second per-attempt timeout
                  retry(2),
                  catchError((error) => {
                    console.error(
                      'Error in createOrUpdateOrderMetaDataFromBuckets (MultiPay):',
                      error
                    );

                    // Log error to Directus for monitoring
                    this.expo
                      .logError(
                        'capacity_service',
                        error.message || error.toString(),
                        {
                          oloId: previousBasket.oloid,
                          basketId: state.basket.id,
                          restaurantId: restaurantId,
                        }
                      )
                      .subscribe();

                    // Don't block customer experience for backend metadata failures
                    return of(null);
                  })
                );

                // Wait for both calls to complete (or timeout/fail gracefully)
                // so the Expo record is guaranteed to be sent before the action resolves.
                // Mirrors the single-CC pattern in submitAndSendData.
                return combineLatest([expoCall$, capacityCall$]).pipe(
                  take(1),
                  map(() => previousBasket)
                );
              })
            );
        })
      );
  }

  @Action(CleanGiftCard)
  cleanGiftCard({getState, patchState}: StateContext<BasketStateModel>) {
    patchState({giftCard: null});
  }

  @Action(EmptyBasket)
  emptyBasket({getState, patchState}: StateContext<BasketStateModel>) {
    patchState({basket: null, capacity: new Limiters([])});
  }

  @Action(EmptyPreviousBasket)
  emptyPreviousBasket({getState, patchState}: StateContext<BasketStateModel>) {
    // Clearing residual state between orders must not strip a signed-in
    // customer's identity: order-details seeds SetDineInName from these
    // fields and the confirmation email uses emailaddress. Mirrors the
    // app-load seeding in shell.component.
    const billingInfo = new BillingInfo();
    const user = this.store.selectSnapshot(
      (state: GlobalStateModel) => state.user
    );
    if (user?.info?.authtoken) {
      billingInfo.usertype = 'user';
      billingInfo.firstname = user.info.firstname;
      billingInfo.lastname = user.info.lastname;
      billingInfo.emailaddress = user.info.emailaddress;
      billingInfo.contactnumber = user.info.contactnumber;
    }
    patchState({
      previousBasket: null,
      billingInfo,
      trancloudInfo: null,
      oloPayReceipt: null,
      change: null,
    });
  }

  @Action(EmptyValidation)
  emptyValidation(ctx: StateContext<BasketStateModel>) {
    return ctx.patchState({
      basketValidation: null,
    });
  }

  @Action(TransferBasket)
  transferBasket(
    {getState, patchState}: StateContext<BasketStateModel>,
    payload: TransferBasket
  ) {
    const basketId = getState().basket.id;
    return this.basketService.transferBasket(payload.vendorId, basketId).pipe(
      tap(async (basketTransfer: BasketTransfer) => {
        // { basket: Basket, itemsNotTransferred: string[] }
        await Preferences.set({
          key: 'punchBasket',
          value: basketTransfer.basket.id,
        });
        if (
          basketTransfer.itemsnottransferred &&
          basketTransfer.itemsnottransferred.length > 0
        ) {
          basketTransfer.itemsnottransferred.forEach((productId) => {
            this.toast.warning(
              'Item not able to be transferred: #' + productId
            );
          });
        }
        this.applyBasket(patchState, basketTransfer.basket);
      })
    );
  }

  @Action(AddUpsellToBasket)
  addUpsellToBasket(
    {getState, patchState}: StateContext<BasketStateModel>,
    payload: AddUpsellToBasket
  ) {
    const before = getState().basket;
    const map = this.productCategoryMap();
    return this.basketService.addUpsellToBasket(before.id, payload.items).pipe(
      switchMap((basket: Basket) =>
        this.rollBackIfOverLimit(patchState, basket, before, map)
      ),
      tap((basket: Basket) => this.applyBasket(patchState, basket))
    );
  }

  @Action(SetChange)
  setChange(ctx: StateContext<BasketStateModel>, action: SetChange) {
    return ctx.patchState({
      change: action.change,
    });
  }

  @Action(ClearChange)
  clearChange(ctx: StateContext<BasketStateModel>, action: ClearChange) {
    return ctx.patchState({
      change: null,
    });
  }

  @Action(ClearExtraCard)
  clearExtraCard(ctx: StateContext<BasketStateModel>, action: ClearChange) {
    return ctx.patchState({
      extraCard: null,
    });
  }

  @Action(StoreTrancloudCardData)
  storeTrancloudCardData(
    ctx: StateContext<BasketStateModel>,
    action: StoreTrancloudCardData
  ) {
    return ctx.patchState({
      trancloudInfo: {
        cardNumber: action.cardNumber,
        cardType: action.cardType,
        authCode: action.authCode,
        refNo: action.refNo,
      },
    });
  }

  @Action(ClearTrancloudCardData)
  clearTrancloudCardData(
    ctx: StateContext<BasketStateModel>,
    action: ClearTrancloudCardData
  ) {
    return ctx.patchState({
      trancloudInfo: null,
    });
  }

  @Action(SetCustomField)
  setCustomField(ctx: StateContext<BasketStateModel>, action: SetCustomField) {
    return this.basketService
      .setBasketCustomField(
        ctx.getState().basket.id,
        action.customFieldID,
        action.value
      )
      .pipe(
        map((basket) => {
          return this.applyBasket(ctx.patchState, basket);
        })
      );
  }

  @Action(SetReferralToken)
  setReferralToken(
    ctx: StateContext<BasketStateModel>,
    action: SetReferralToken
  ) {
    const token = sessionStorage.getItem('rwg_token');
    sessionStorage.removeItem('rwg_token');
    return this.basketService
      .addReferralToBasket(ctx.getState().basket.id, token, 'rwg_token')
      .pipe(
        map(() => {}),
        catchError((error) => {
          // Swallowed on purpose — a failed referral attach must not block the
          // order — but silently, so a systematically broken Reserve-with-Google
          // attribution would never show up. Report and continue.
          reportHandledError(error, {
            source: 'basket-referral-token-attach-failed',
            extra: {basketId: ctx.getState().basket?.id},
          });
          return of();
        })
      );
  }

  @Action(StoreOloPayReceiptData)
  storeOloPayReceiptData(
    {patchState}: StateContext<BasketStateModel>,
    {payload}: StoreOloPayReceiptData
  ) {
    patchState({oloPayReceipt: {...payload}});
  }

  @Action(ClearOloPayReceiptData)
  clearOloPayReceiptData({patchState}: StateContext<BasketStateModel>) {
    patchState({
      oloPayReceipt: undefined,
    });
  }

  /**
   * Handles payment processing for OloPay terminals using payment session API
   */
  private oloPayCardSwipe(
    body: BillingInfo,
    authtoken: string,
    restaurantId: number,
    buckets: SelectableTime[],
    user: any,
    tableNumber: string,
    {patchState, getState, setState, dispatch}: StateContext<BasketStateModel>
  ) {
    const state = getState();

    /* ── 1. Locate payment-terminal billing scheme ───────────────────────── */
    const paymentTerminalScheme = state.billingSchemes.find(
      (scheme: any) => scheme.type === 'paymentterminal'
    );

    if (!paymentTerminalScheme) {
      this.toastr.warning('Payment terminal billing scheme not found');
      return throwError(
        () => new Error('Payment terminal billing scheme not found')
      );
    }

    /* ── 2. Use OloPay service to process payment ──────────── */
    // Amount MUST be read from current basket at call time — see fix-terminal-residual-state.
    return this.oloPayPayment
      .processCardPayment(
        state.basket.id,
        paymentTerminalScheme.id,
        state.basket.total
      )
      .pipe(
        switchMap((result) => {
          if (result.status !== 'Approved') {
            const msg =
              result.status === 'Declined' ? 'Card Declined' : 'Terminal Error';
            this.toastr.warning(msg);
            return throwError(() => new Error(msg));
          }

          /* ── 3. Prep billing-info for Olo (paymentsession) ──────────── */
          body.billingmethod = 'paymentsession';
          body.paymentsessionid = result.paymentSessionId;
          body.billingschemeid = result.billingSchemeId;

          /* ── 4. Persist to NGXS for receipt/print components ────────── */
          this.store.dispatch(
            new StoreTrancloudCardData(
              result.cardNumber,
              result.cardType,
              result.authCode,
              result.refNo
            )
          );

          /* ── 5. Hand off to the shared order-submitter ──────────────── */
          return this.submitAndSendData(
            body,
            authtoken,
            restaurantId,
            buckets,
            user,
            {patchState, getState, setState, dispatch},
            tableNumber,
            result.refNo, // invoiceNumber
            result.cardNumber,
            result.cardType,
            result.authCode,
            result.refNo, // refNumber
            result.paymentSessionId, // recordNumber
            '', // processData  (none for Olo)
            '' // acqRefData   (none for Olo)
          );
        }),
        catchError((err) => {
          console.error('Error with OloPay payment:', err);
          this.toastr.warning(err.message || 'Payment terminal error');
          return throwError(() => err);
        })
      );
  }

  /**
   * Olo Accounts opt-in: when the guest checked "save my info" on the
   * guest-info screen and verified their phone via Olo's
   * CreateAccountCheckbox, append the account-creation flag + phone
   * confirmation token to the submit body so Olo creates the user record.
   * Olo documents `oloaccount` on both the single-payment and
   * multiple-payments submit endpoints — keep both paths calling this.
   */
  private appendOloAccountIntent(
    body: Pick<BillingInfo, 'oloaccount'>,
    state: BasketStateModel
  ): void {
    if (state.createAccountOptedIn && state.oloAccountConfirmationToken) {
      body.oloaccount = {
        createaccount: true,
        confirmationtoken: {
          token: state.oloAccountConfirmationToken,
          verificationmethod: state.oloAccountVerificationMethod ?? 'sms',
        },
      };
    }
  }

  /**
   * Surface Olo's account-creation confirmation if the guest opted in.
   * Olo returns `oloaccountcreated: { created: boolean }` on the submit
   * response when `oloaccount.createaccount: true` was set. No authtoken
   * is returned for the new account here — the guest signs in via the
   * borderless overlay on their next visit. We just acknowledge so they
   * know it took. Never throws — called post-charge.
   */
  private acknowledgeOloAccountCreated(
    state: BasketStateModel,
    previousBasket: Order
  ): void {
    try {
      if (
        state.createAccountOptedIn &&
        previousBasket.oloaccountcreated?.created
      ) {
        this.toast.success(
          'Your account is set up — next time you order, sign in to see your saved details.'
        );
      }
    } catch (e) {
      // The toast is cosmetic — a failure here must never break the
      // post-charge flow (navigation to /confirm, CMS metadata).
      console.error('Failed to acknowledge Olo account creation', e);
    }
  }

  private submitAndSendData(
    body: any,
    authtoken: string,
    restaurantId: number,
    buckets: SelectableTime[],
    user: any,
    {patchState, getState, setState, dispatch}: StateContext<BasketStateModel>,
    tableNumber: string,
    invoiceNumber: string,
    cardNumber: string,
    cardType: string,
    authCode: string,
    refNo: string,
    recordNo: string,
    processData: string,
    acpRefData: string
  ): Observable<any> {
    const state = getState();
    this.appendOloAccountIntent(body, state);
    console.log(
      'submitAndSendData 1509',
      cardNumber,
      cardType,
      authCode,
      refNo,
      recordNo
    );
    if (!cardNumber) {
      const info = getState().trancloudInfo;
      if (info) {
        cardNumber = info.cardNumber;
        cardType = info.cardType;
        authCode = info.authCode;
        refNo = info.refNo;
      }
    }
    console.log(
      'submitAndSendData 1519',
      cardNumber,
      cardType,
      authCode,
      refNo,
      recordNo
    );
    const directusIds = state?.capacityPlaceholder?.directusId || null;

    // Submit order to Olo FIRST - this is the critical path
    return this.basketService
      .submitOrder(state.basket.id, body, authtoken)
      .pipe(
        catchError((error) => {
          // Report the real Olo failure before every branch below replaces it
          // with a user-facing string. Without this, a failed submit reaches
          // Sentry (if at all) as a bare message with no status code, response
          // body, or basket id — the exact context needed to tell "Olo rejected
          // the order" apart from "the order went through and we lost the
          // response".
          reportHandledError(error, {
            source: 'basket-submit-order-failed',
            extra: {
              basketId: state.basket?.id,
              isOloPayTerminal: this.oloPayTerminal.isOloPayTerminal(),
              paymentSessionId: body.paymentsessionid ?? null,
              mode: this.mode.getStoredMode(),
            },
          });
          // Check if using Olo Pay terminal for cancellation
          if (this.oloPayTerminal.isOloPayTerminal()) {
            const paymentSessionId = body.paymentsessionid;
            if (paymentSessionId) {
              const paymentTerminalScheme = state.billingSchemes.find(
                (scheme: any) => scheme.type === 'paymentterminal'
              );
              if (paymentTerminalScheme) {
                return this.basketService
                  .cancelAPaymentSession(
                    state.basket.id,
                    paymentTerminalScheme.id,
                    paymentSessionId
                  )
                  .pipe(
                    // Bounded so a hanging void can't leave the kiosk spinning
                    // with no warning, matching the kiosk-side void.
                    timeout(8000),
                    retry(2),
                    // catchError must sit before the success sentinel below, or
                    // it would catch that sentinel and report a successful void
                    // as a failed one.
                    catchError((cancelError) => {
                      // Worst case in the whole payment flow: the order did not
                      // go through AND releasing the terminal's authorization
                      // failed, so the customer can be left with a hold on their
                      // card that nobody knows about. Report it as its own issue
                      // rather than letting the retry message be the only trace.
                      reportHandledError(cancelError, {
                        source: 'basket-payment-auth-cancel-failed',
                        extra: {
                          basketId: state.basket?.id,
                          paymentSessionId: body.paymentsessionid ?? null,
                          mode: this.mode.getStoredMode(),
                        },
                      });
                      return throwError(
                        'Order could not be submitted. Payment authorization could not be cancelled.'
                      );
                    }),
                    switchMap(() => throwError(PAYMENT_AUTHORIZATION_VOIDED))
                  );
              } else {
                return throwError(error);
              }
            } else {
              return throwError(error);
            }
          } else {
            return throwError(error);
          }
        }),
        switchMap((previousBasket: Order) => {
          // Submission Successful - persist immediately so UI can navigate to confirmation.
          // Shared with SubmitBasketMultiPay via buildPostSubmitPatch — see
          // openspec/changes/fix-terminal-residual-state.
          (previousBasket as any).basket_id = state.basket.id;
          this.acknowledgeOloAccountCreated(state, previousBasket);
          patchState(buildPostSubmitPatch(previousBasket));
          this.releaseGuestSession();
          try {
            sessionStorage.setItem('punchPreviousOrder', previousBasket.oloid);
          } catch {}

          // Order placed - send meta data to Directus (Capacity)
          Preferences.remove({key: 'punchBasket'});
          // The customer has already been charged: nothing below may throw
          // out of this switchMap, or the action errors, navigation to
          // /confirm is skipped, and the CMS never receives the order.
          let enzoQuant = 0;
          try {
            const restaurant = this.store.selectSnapshot(
              (state: GlobalStateModel) => state.restaurant
            );
            state.basket.products.forEach((p: BasketProduct) => {
              p.choices.forEach((c: BasketChoice) => {
                if (c.name === restaurant.settings.enzo_modifier_name) {
                  enzoQuant = enzoQuant + 1 * p.quantity;
                }
              });
            });
          } catch (e) {
            console.error('Failed to compute enzo quantity', e);
          }

          // NOTE: Do NOT dispatch CreateBasket here. Creating a fresh basket
          // while the user is still on checkout allows a second click to submit
          // a real duplicate order. The next order flow (menu, locations, kiosk)
          // will create a new basket when the customer starts ordering again.

          try {
            this.analytics.logPurchase(
              state.basket,
              previousBasket,
              authtoken ? 'user' : 'guest'
            );
          } catch (e) {
            console.error('Analytics failed to log purchase', e);
          }

          // Create Expo order with retries - wait briefly but don't block indefinitely.
          // Operator order is timeout → retry → catchError so each retry attempt gets
          // its own 5 s budget. See openspec/changes/fix-expo-metadata-per-call-timeout.
          // defer() so a synchronous throw while building the request body
          // (e.g. a null field on wiped user state) is routed into catchError
          // + logError below instead of escaping the switchMap post-charge.
          const expoCall$ = defer(() =>
            this.expo.postExpoOrder(
              previousBasket,
              user,
              state.manualFireTime,
              tableNumber,
              invoiceNumber,
              cardNumber,
              cardType,
              authCode,
              refNo,
              recordNo,
              state.change ? state.change + state.basket.total : null,
              state.change
            )
          ).pipe(
            timeout(5000), // 5 second per-attempt timeout
            retry(3),
            catchError((error) => {
              console.error('Error in postExpoOrder:', error);

              // Log error to Directus for monitoring
              this.expo
                .logError('expo_service', error.message || error.toString(), {
                  oloId: previousBasket.oloid,
                  basketId: state.basket.id,
                  restaurantId: restaurantId,
                })
                .subscribe();

              // Don't block customer experience for backend expo metadata failures
              return of(null);
            })
          );

          // Create capacity metadata with retries - wait briefly but don't block indefinitely.
          // Operator order is timeout → retry → catchError so each retry attempt gets
          // its own 5 s budget. See openspec/changes/fix-expo-metadata-per-call-timeout.
          const capacityCall$ = defer(() =>
            this.capacityService.createOrUpdateOrderMetaDataFromBuckets(
              restaurantId,
              previousBasket.oloid,
              buckets,
              false,
              enzoQuant,
              state.basket.deliverymode === 'pickup' &&
                localStorage.getItem('modeKey') === 'order',
              directusIds
            )
          ).pipe(
            timeout(5000), // 5 second per-attempt timeout
            retry(2),
            catchError((error) => {
              console.error(
                'Error in createOrUpdateOrderMetaDataFromBuckets:',
                error
              );

              // Log error to Directus for monitoring
              this.expo
                .logError(
                  'capacity_service',
                  error.message || error.toString(),
                  {
                    oloId: previousBasket.oloid,
                    basketId: state.basket.id,
                    restaurantId: restaurantId,
                  }
                )
                .subscribe();

              // Don't block customer experience for backend metadata failures
              return of(null);
            })
          );

          // Wait for both calls to complete (or timeout/fail gracefully)
          // This ensures reliability while still protecting against indefinite blocks
          return combineLatest([expoCall$, capacityCall$]).pipe(
            take(1),
            map(() => previousBasket)
          );
        })
      );
  }
}
